import { useCallback, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { format, isAfter, isSameDay, parse, startOfMonth } from "date-fns";

import { DateNavigator } from "~/screens/Admins/components/DateNavigator";
import { MultiSelectDropdownInput } from "~/screens/Admins/components/MultiSelectDropdownInput";
import type { MultiSelectOption } from "~/screens/Admins/components/MultiSelectDropdownInput";
import { TIMEZONES } from "~/screens/Scheduling/InitialVisit/utils/date-time-utils";
import {
  SLOT_STATUSES,
  slotStatusLabels,
  VISIT_TYPES,
  visitTypeLabels,
} from "~/services";
import type { VisitType } from "~/services";
import {
  useConvertProviderSlotMutation,
  useGetProviderSlots,
  useGetProviderSlotsInfinite,
} from "~/services/admin-providers";
import type {
  ProviderSlotFilters,
  ProviderSlotRow,
} from "~/services/admin-providers";
import { parseSlotDate, SLOT_DATE_FORMAT } from "~/shared/utils/slot-date";
import { icons } from "~/ui";
import { CompactTable } from "~/ui/table/CompactTable";
import { dateToUsFormat, parseApiErrorMessage, tw } from "~/utils";
import { ConvertSlotConfirmation } from "./ConvertSlotConfirmation";

type Props = {
  providerId: string;
};

const ALL_SLOTS_HEIGHT_CLASS = "max-h-64 min-h-64";
const SLOT_COLUMN_SIZES = { dateTime: 170, visitType: 190, status: 110 };

const VISIT_TYPE_FILTER_OPTIONS: MultiSelectOption[] = [
  VISIT_TYPES.INITIAL,
  VISIT_TYPES.FOLLOW_UP,
  VISIT_TYPES.COACH,
].map((visitType) => ({ id: visitType, label: visitTypeLabels[visitType] }));

const STATUS_FILTER_OPTIONS: MultiSelectOption[] = [
  SLOT_STATUSES.OPEN,
  SLOT_STATUSES.BOOKED,
].map((status) => ({ id: status, label: slotStatusLabels[status] }));

const FILTER_LABEL_CLASS = "text-sm";
const FILTER_OPTIONS_CLASS = "left-0 right-auto origin-top-left";

const ALL_VISIT_TYPE_IDS = VISIT_TYPE_FILTER_OPTIONS.map((option) => option.id);
const ALL_STATUS_IDS = STATUS_FILTER_OPTIONS.map((option) => option.id);

const CONVERSION_TARGET_VISIT_TYPE: Partial<Record<VisitType, VisitType>> = {
  [VISIT_TYPES.INITIAL]: VISIT_TYPES.FOLLOW_UP,
  [VISIT_TYPES.FOLLOW_UP]: VISIT_TYPES.INITIAL,
};

const slotStartAt = (slot: ProviderSlotRow) =>
  `${format(parseSlotDate(slot.date), "yyyy-MM-dd")} ${slot.startTime}:00`;

const timeColumn: ColumnDef<ProviderSlotRow> = {
  header: "Time",
  accessorKey: "startTime",
  size: SLOT_COLUMN_SIZES.dateTime,
  cell: ({ row }) => {
    const timeDate = parse(
      row.original.startTime,
      "HH:mm",
      parseSlotDate(row.original.date),
    );

    return `${format(timeDate, "h:mm a")} ${TIMEZONES.ET.code}`;
  },
};

const dateTimeColumn: ColumnDef<ProviderSlotRow> = {
  header: "Date/Time",
  accessorKey: "date",
  size: SLOT_COLUMN_SIZES.dateTime,
  cell: ({ row }) =>
    dateToUsFormat(slotStartAt(row.original), TIMEZONES.ET.code),
};

const statusColumn: ColumnDef<ProviderSlotRow> = {
  header: "Status",
  accessorKey: "status",
  size: SLOT_COLUMN_SIZES.status,
  cell: ({ row }) => {
    const status = row.original.status ?? "";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  },
};

export const AppointmentListSection = ({ providerId }: Props) => {
  const [isAllViewMode, setIsAllViewMode] = useState(false);
  const [selectedVisitTypes, setSelectedVisitTypes] =
    useState<string[]>(ALL_VISIT_TYPE_IDS);
  const [selectedStatuses, setSelectedStatuses] =
    useState<string[]>(ALL_STATUS_IDS);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [calendarMonth, setCalendarMonth] = useState({
    year: selectedDate.getFullYear(),
    monthIndex: selectedDate.getMonth(),
  });

  const startOfSelectedMonth = startOfMonth(
    new Date(calendarMonth.year, calendarMonth.monthIndex),
  );

  const startDate = format(startOfSelectedMonth, SLOT_DATE_FORMAT);
  const { data: providerSlots, isPending } = useGetProviderSlots({
    providerId,
    startDate,
  });

  const hasEmptyFilterGroup =
    selectedVisitTypes.length === 0 || selectedStatuses.length === 0;

  const requestFilters: ProviderSlotFilters = {
    visitTypes:
      selectedVisitTypes.length === ALL_VISIT_TYPE_IDS.length
        ? []
        : selectedVisitTypes,
    statuses:
      selectedStatuses.length === ALL_STATUS_IDS.length ? [] : selectedStatuses,
  };

  const hasNarrowedFilters =
    requestFilters.visitTypes.length > 0 || requestFilters.statuses.length > 0;

  const allSlots = useGetProviderSlotsInfinite({
    providerId,
    enabled: isAllViewMode && !hasEmptyFilterGroup,
    ...requestFilters,
  });

  const [slotToConvert, setSlotToConvert] = useState<ProviderSlotRow | null>(
    null,
  );

  const convertSlotMutation = useConvertProviderSlotMutation(
    { providerId },
    { onSuccess: () => setSlotToConvert(null) },
  );

  const currentVisitType = slotToConvert?.appointmentType as
    VisitType | undefined;
  const targetVisitType = currentVisitType
    ? CONVERSION_TARGET_VISIT_TYPE[currentVisitType]
    : undefined;

  const { reset: resetConversionMutation } = convertSlotMutation;

  const openConversion = useCallback(
    (slot: ProviderSlotRow) => {
      resetConversionMutation();
      setSlotToConvert(slot);
    },
    [resetConversionMutation],
  );

  const closeConversion = () => {
    convertSlotMutation.reset();
    setSlotToConvert(null);
  };

  const confirmConversion = () => {
    if (!slotToConvert?.appointmentType) return;

    convertSlotMutation.mutate({
      slotDate: format(parseSlotDate(slotToConvert.date), "yyyy-MM-dd"),
      startTime: slotToConvert.startTime,
      currentAppointmentTypeSlug: slotToConvert.appointmentType,
    });
  };

  const isSelectableDay = (date: Date) => {
    const key = format(date, SLOT_DATE_FORMAT);
    const slots = providerSlots?.[key];
    const today = new Date();
    const isAfterToday = isAfter(date, today) || isSameDay(date, today);

    return isAfterToday && Array.isArray(slots) && slots.length > 0;
  };

  const handleDateChange = (date: Date) => {
    if (
      date.getFullYear() !== calendarMonth.year ||
      date.getMonth() !== calendarMonth.monthIndex
    ) {
      setCalendarMonth({
        year: date.getFullYear(),
        monthIndex: date.getMonth(),
      });
    }
    setSelectedDate(date);
  };

  const { hasNextPage, isFetching, fetchNextPage } = allSlots;

  const handleReachEnd = useCallback(() => {
    if (!hasNextPage || isFetching) return;

    void fetchNextPage({ cancelRefetch: false });
  }, [hasNextPage, isFetching, fetchNextPage]);

  const dateKey = format(selectedDate, SLOT_DATE_FORMAT);
  const slotsPerDay = useMemo<ProviderSlotRow[]>(
    () =>
      (providerSlots?.[dateKey] ?? []).map((slot) => ({
        ...slot,
        date: dateKey,
      })),
    [providerSlots, dateKey],
  );

  const visitTypeColumn = useMemo<ColumnDef<ProviderSlotRow>>(
    () => ({
      header: "Visit Type",
      accessorKey: "appointmentType",
      size: SLOT_COLUMN_SIZES.visitType,
      cell: ({ row }) => {
        const visitType = row.original.appointmentType as VisitType;

        return (
          <div className="flex items-start justify-between gap-2">
            <span>{visitTypeLabels[visitType] || visitType || "-"}</span>
            <button
              type="button"
              aria-label="Convert visit type"
              disabled={!row.original.isConvertible}
              className={tw(
                "shrink-0 cursor-pointer",
                !row.original.isConvertible && "invisible",
              )}
              onClick={() => openConversion(row.original)}
            >
              <icons.outline.ArrowsRightLeftIcon className="size-4" />
            </button>
          </div>
        );
      },
    }),
    [openConversion],
  );

  const allSlotsColumns = useMemo(
    () => [dateTimeColumn, visitTypeColumn, statusColumn],
    [visitTypeColumn],
  );

  const slotsPerDayColumns = useMemo(
    () => [timeColumn, visitTypeColumn, statusColumn],
    [visitTypeColumn],
  );

  const allSlotsContent = hasEmptyFilterGroup ? (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
      Nothing selected. Check at least one visit type and one status to see
      results.
    </div>
  ) : (
    <CompactTable
      data={allSlots.rows}
      columns={allSlotsColumns}
      emptyState={
        allSlots.isError
          ? "Slots could not be loaded. Close and reopen the modal to retry."
          : hasNarrowedFilters
            ? "No slots match the selected filters."
            : "No slots available."
      }
      isLoading={allSlots.isPending || allSlots.hasNextPage}
      maxHeightClassName={ALL_SLOTS_HEIGHT_CLASS}
      virtualized
      onReachEnd={handleReachEnd}
    />
  );

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full items-center justify-between gap-4">
          {isAllViewMode ? (
            <div className="flex gap-4">
              <MultiSelectDropdownInput
                options={VISIT_TYPE_FILTER_OPTIONS}
                selected={selectedVisitTypes}
                onChange={setSelectedVisitTypes}
                allLabel="All visit types"
                labelClassName={FILTER_LABEL_CLASS}
                optionsClassName={FILTER_OPTIONS_CLASS}
              />

              <MultiSelectDropdownInput
                options={STATUS_FILTER_OPTIONS}
                selected={selectedStatuses}
                onChange={setSelectedStatuses}
                allLabel="All statuses"
                labelClassName={FILTER_LABEL_CLASS}
                optionsClassName={FILTER_OPTIONS_CLASS}
              />
            </div>
          ) : (
            <DateNavigator
              selectedDate={selectedDate}
              setSelectedDate={handleDateChange}
              calendarMonth={calendarMonth}
              setCalendarMonth={setCalendarMonth}
              isSelectableDay={isSelectableDay}
              isPending={isPending}
            />
          )}
          <button
            type="button"
            className="text-indigo ml-auto shrink-0 cursor-pointer text-sm underline"
            onClick={() => setIsAllViewMode(!isAllViewMode)}
          >
            {isAllViewMode ? "View slots by day" : "View all slots"}
          </button>
        </div>

        {isAllViewMode ? (
          allSlotsContent
        ) : (
          <CompactTable data={slotsPerDay} columns={slotsPerDayColumns} />
        )}
      </div>

      {currentVisitType && targetVisitType ? (
        <ConvertSlotConfirmation
          show={!!slotToConvert}
          currentVisitType={currentVisitType}
          targetVisitType={targetVisitType}
          isLoading={convertSlotMutation.isPending}
          errorMessage={
            convertSlotMutation.error
              ? parseApiErrorMessage(convertSlotMutation.error)
              : undefined
          }
          onClose={closeConversion}
          onConfirm={confirmConversion}
        />
      ) : null}
    </div>
  );
};
